import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/auth'

// Auth Default Routes
const authChildRoutes = (prefix) => [
  {
    path: 'login',
    name: prefix + '.login',
    meta: { public: true, guestOnly: true, name: 'Login' },
    component: () => import('@/views/auth/default/SignIn.vue')
  },
  {
    path: 'verify',
    name: prefix + '.verify',
    meta: { public: true, guestOnly: true, name: 'Verify Discord Login' },
    component: () => import('@/views/auth/default/VerifyDiscordAccount.vue')
  },
]
// Default routes
const defaultChildRoutes = (prefix) => [
  {
    path: '/',
    name: prefix + '.dashboard',
    meta: { requiresAuth: true, name: 'Home', isBanner: true },
    component: () => import('@/views/dashboards/IndexPage.vue')
  },

  // Discord
  {
    path: '/discord/annonce',
    name: prefix + '.discord.annonce',
    meta: { requiresAuth: true, name: 'Annonce', isBanner: true },
    component: () => import('@/views/discord/AnnoncePage.vue')
  },
  {
    path: '/discord/changelog',
    name: prefix + '.discord.changelog',
    meta: { requiresAuth: true, name: 'Changelog', isBanner: true },
    component: () => import('@/views/discord/ChangelogPage.vue')
  },
  {
    path: '/discord/tickets',
    name: prefix + '.discord.tickets',
    meta: { requiresAuth: true, name: 'Tickets', isBanner: true },
    component: () => import('@/views/discord/TicketsPage.vue')
  },
  {
    path: '/discord/subscriptions',
    name: prefix + '.discord.subscriptions',
    meta: { requiresAuth: true, name: 'Subscriptions', isBanner: true },
    component: () => import('@/views/discord/SubscriptionsListPage.vue')
  },

  // Users Pages
  {
    path: '/user-profile',
    name: prefix + '.user-profile',
    meta: { requiresAuth: true, name: 'User Add', isBanner: true },
    component: () => import('@/views/user/ProfilePage.vue')
  },

  // Admin
  {
    path: '/admin/user-list',
    name: prefix + '.user-list',
    meta: { requiresAuth: true, name: 'User List', isBanner: true },
    component: () => import('@/views/admin/UserListPage.vue')
  },
  {
    path: '/admin/roles-permissions',
    name: prefix + '.roles-permissions',
    meta: { requiresAuth: true, name: 'Rôles & Permissions', isBanner: true },
    component: () => import('@/views/admin/RolePage.vue')
  },

  // Extra Pages
  {
    path: '/privacy-policy',
    name: prefix + '.privacy-policy',
    meta: { requiresAuth: true, name: 'Privacy Policy', isBanner: true },
    component: () => import('@/views/extra/PrivacyPolicy.vue')
  },
  {
    path: '/terms-and-conditions',
    name: prefix + '.terms-and-conditions',
    meta: { requiresAuth: true, name: 'Terms and Conditions', isBanner: true },
    component: () => import('@/views/extra/TermsAndConditions.vue')
  },
  {
    path: '/icons/solid',
    name: prefix + '.icons.solid',
    meta: { requiresAuth: true, name: 'Solid Icon', isBanner: true },
    component: () => import('@/views/icons/SolidIcon.vue')
  },
]

const errorRoutes = (prefix) => [
  // Error Pages
  {
    path: '404',
    name: prefix + '.404',
    meta: { requiresAuth: true, name: 'Error 404', isBanner: true },
    component: () => import('@/views/errors/Error404Page.vue')
  },
  {
    path: '500',
    name: prefix + '.500',
    meta: { requiresAuth: true, name: 'Error 500', isBanner: true },
    component: () => import('@/views/errors/Error500Page.vue')
  },
  {
    path: 'maintenance',
    name: prefix + '.maintenance',
    meta: { requiresAuth: true, name: 'Maintenance', isBanner: true },
    component: () => import('@/views/errors/MaintenancePage.vue')
  }
]

const routes = [
  // Default Pages
  {
    path: '/',
    name: 'dashboard',
    component: () => import('../layouts/DefaultLayout.vue'),
    children: defaultChildRoutes('default')
  },
  // Auth Skins
  {
    path: '/auth',
    name: 'auth',
    component: () => import('../layouts/guest/BlankLayout.vue'),
    children: authChildRoutes('auth')
  },
  // Errors Pages
  {
    path: '/errors',
    name: 'errors',
    component: () => import('../layouts/guest/BlankLayout.vue'),
    children: errorRoutes('errors')
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/errors/404'
  }
]

const router = createRouter({
  linkActiveClass: 'active',
  linkExactActiveClass: 'exact-active',
  history: createWebHistory("/"),
  base: "/",
  routes
})

// Guard de navigation
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const token = localStorage.getItem('auth_token')

  // Charger l'utilisateur si un token existe mais pas d'user dans le store
  if (token && !authStore.user) {
    await authStore.fetchUser()
  }

  // Si l'utilisateur est connecté et essaie d'accéder à une page "guestOnly"
  if (to.meta.guestOnly && authStore.isAuthenticated) {
    next('/') // Rediriger vers le dashboard
    return
  }

  // Si la route nécessite l'authentification
  if (to.meta.requiresAuth) {
    if (!token) {
      next('/auth/login')
      return
    }

    if (!authStore.user) {
      const success = await authStore.fetchUser()

      if (!success) {
        next('/auth/login')
        return
      }
    }

    next()
    return
  }

  // Routes publiques (ni requiresAuth ni guestOnly)
  next()
})

export default router
